import { ArrowUUpLeftIcon, CalendarBlankIcon, UserIcon } from '@phosphor-icons/react'
import { useSelector } from 'react-redux'

import { ReservationDetails } from '@/api/reservations/get-reservation-details'
import { Badge } from '@/components/ui/badge'
import { Icon } from '@/components/ui/icon'
import { Typography } from '@/components/ui/typography'
import { DEFAULT_TZ } from '@/constants'
import { getAssetLink } from '@/helpers/get-asset-link'
import { selectActiveBuilding } from '@/reducers/buildingSelectorReducer'

import { getPaymentInfo, getReservationInfo } from './reservation-details.utils'
import * as S from './reservations-details.styled'

interface Props {
  reservation?: ReservationDetails
}

export const Header = ({ reservation }: Props) => {
  const activeBuilding = useSelector(selectActiveBuilding)
  const timezone = activeBuilding?.timezone ?? DEFAULT_TZ

  const file = reservation?.locationId?.image ?? reservation?.amenityId?.image
  const src = getAssetLink({
    fallback: { file: 'default_amenity.png' },
    file,
    path: 'chitown-building',
  })
  const locationName = reservation?.locationId?.name ?? ''
  const reservationName = `${reservation?.amenityId?.name ?? ''}${
    locationName ? `, ${locationName}` : ''
  }`
  const user = reservation?.clientId?.user ?? reservation?.guestId
  const username = [user?.firstName, user?.lastName].filter(Boolean).join(' ')
  const unit = reservation?.clientId ? reservation?.clientId.apartment : undefined

  const dateString = getReservationInfo({
    date: reservation?.date,
    endDate: reservation?.endDate,
    endTime: reservation?.endTime,
    startTime: reservation?.startTime,
    timezone,
  })

  const { label, amount, pill, strike } = getPaymentInfo({
    chargeAmount: reservation?.chargeAmount,
    isCancelled: reservation?.cancelled,
    isPaidAmenity: reservation?.amenityId?.isPaid,
    refundAmount: reservation?.refundAmount,
  })

  return (
    <S.Header>
      <S.HeaderMain>
        <S.HeaderImageFrame src={src} title={reservationName}>
          <S.HeaderImage
            src={src}
            alt={'Reservation image'}
            onError={(event) => {
              event.currentTarget.onerror = null
              event.currentTarget.src = getAssetLink({
                file: 'default_amenity.png',
                path: 'chitown-building',
              })
            }}
          />
        </S.HeaderImageFrame>

        <S.BasicInfoContainer>
          <Typography color="black800" size="heading-large" weight="bold">
            {reservationName}
          </Typography>
          <S.BasicInfo color="primary600" weight="medium" size="text-medium">
            <Icon icon={CalendarBlankIcon} />
            {dateString}
          </S.BasicInfo>
          <S.BasicInfo color="primary600" weight="medium" size="text-medium">
            <Icon icon={UserIcon} />
            {`${username}${unit ? `, ${unit}` : ''}`}
          </S.BasicInfo>
        </S.BasicInfoContainer>
      </S.HeaderMain>

      <S.HeaderDivider color="gray300" />

      <S.PaidInfo>
        {label && (
          <Typography color="primary600" weight="medium" size="text-medium">
            {label}
          </Typography>
        )}

        {amount && (
          <Typography
            color="black800"
            size="heading-large"
            weight="bold"
            style={{ textDecoration: strike ? 'line-through' : undefined }}
          >
            {amount}
          </Typography>
        )}

        {pill && (
          <S.PaidBadgeWrapper>
            <Badge color={pill.color} size="small" variant={pill.variant ?? 'solid'}>
              {pill.text}
              {pill.refunded && <S.RefundedIcon icon={ArrowUUpLeftIcon} />}
            </Badge>
          </S.PaidBadgeWrapper>
        )}
      </S.PaidInfo>
    </S.Header>
  )
}
